import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { CEFR_LEVELS, type CefrLevel, type LevelAssessmentView } from '@en3/shared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * 等级与得分的历史趋势折线（纯 SVG 手写）。
 *
 * **为什么手写 SVG 而不引图表库**：这里要画的是一条最多 20 个点的折线 + 两条网格线，
 * 引 recharts / chart.js 会带来 40KB+ 的依赖、一套与本站语义化 token 不通用的主题配置，
 * 以及"深色模式下坐标轴字看不清"这类要单独调的问题。手写 SVG 三十行就够，
 * 用 `currentColor` 直接吃 Tailwind 的语义色，深色模式自动跟随，还不需要新增依赖。
 *
 * 另外两个取舍：
 * 1. **纵轴固定 0–100**（不给自适应范围）。自适应会把"B1 → B2 的小幅提升"拉成陡峭的直线，
 *    看着很爽但失真；固定量程下用户看到的是真实幅度。
 * 2. **点少于 2 个时不画折线，只说明原因**。单点折线在视觉上是空白，用户会以为图坏了；
 *    明确写"积累 2 次评估后显示趋势"才是诚实且可行动的。
 */

/** 与 `LevelReportView.history` 的元素形状一致：只取画图需要的字段。 */
type HistoryPoint = Pick<LevelAssessmentView, 'id' | 'cefr' | 'score' | 'vocabEstimate' | 'createdAt'>;

const WIDTH = 320;
const HEIGHT = 160;
const PAD_LEFT = 26;
const PAD_RIGHT = 14;
const PAD_TOP = 14;
const PAD_BOTTOM = 26;

const INNER_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const INNER_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

/** 网格线取值。0/50/100 三条足够读图，画满十条反而干扰。 */
const GRID_VALUES = [0, 50, 100] as const;

function formatShortDate(epochMs: number): string {
  const date = new Date(epochMs);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** CEFR 字母档折算成 0–100，好和得分画在同一张图上。 */
function cefrToValue(cefr: CefrLevel): number {
  const index = CEFR_LEVELS.indexOf(cefr);
  if (index < 0) return 0;
  return (index / (CEFR_LEVELS.length - 1)) * 100;
}

export function TrendChart({ history, className }: { history: HistoryPoint[]; className?: string }) {
  // 接口给的是时间倒序，画图必须正序，否则折线是反的
  const points = useMemo(() => [...history].sort((a, b) => a.createdAt - b.createdAt), [history]);

  const first = points[0];
  const last = points[points.length - 1];

  if (points.length < 2 || !first || !last) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp aria-hidden className="size-4 text-primary" />
            水平趋势
          </CardTitle>
        </CardHeader>
        <CardBody>
          <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            积累 2 次评估后显示趋势。
            <br />
            <span className="text-xs">
              现在有 {points.length} 次记录。多读几篇、复习一阵子后再点「重新评估」，就能看到变化曲线。
            </span>
          </p>
        </CardBody>
      </Card>
    );
  }

  const xAt = (index: number) => PAD_LEFT + (INNER_W * index) / (points.length - 1);
  const yAt = (value: number) => PAD_TOP + INNER_H * (1 - clamp(value, 0, 100) / 100);

  const cefrSeries = points.map((point, index) => ({
    x: xAt(index),
    y: yAt(cefrToValue(point.cefr)),
    cefr: point.cefr,
  }));

  // 得分可能为 null（老数据），null 的点直接不参与连线 —— 用 0 顶替会在图上造出一次暴跌
  const scoreSeries = points.flatMap((point, index) =>
    point.score === null ? [] : [{ x: xAt(index), y: yAt(point.score), score: point.score }],
  );

  const toPath = (series: { x: number; y: number }[]) =>
    series.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');

  // 最新点标注：优先标得分，没有得分就标等级档位（两者都画会挤在一起）
  const latestScore = last.score;
  const markerY = latestScore === null ? yAt(cefrToValue(last.cefr)) : yAt(latestScore);
  const markerX = xAt(points.length - 1);
  const markerText = latestScore === null ? last.cefr : `${Math.round(latestScore)} 分`;

  const description = points
    .map((point) => `${formatShortDate(point.createdAt)} ${point.cefr}${point.score === null ? '' : ` ${Math.round(point.score)} 分`}`)
    .join('；');

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp aria-hidden className="size-4 text-primary" />
          水平趋势
        </CardTitle>
        <span className="text-xs text-muted-foreground">共 {points.length} 次评估</span>
      </CardHeader>

      <CardBody className="space-y-3">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          // 宽高由外层容器决定（viewBox 缩放），375px 屏上不会横向溢出
          className="h-auto w-full"
          role="img"
          aria-label={`水平趋势：${description}`}
        >
          {/* 网格与纵轴刻度 */}
          <g className="text-border">
            {GRID_VALUES.map((value) => (
              <line
                key={value}
                x1={PAD_LEFT}
                x2={WIDTH - PAD_RIGHT}
                y1={yAt(value)}
                y2={yAt(value)}
                stroke="currentColor"
                strokeWidth={value === 0 ? 1 : 0.6}
                strokeDasharray={value === 0 ? undefined : '3 3'}
              />
            ))}
          </g>
          <g className="text-muted-foreground">
            {GRID_VALUES.map((value) => (
              <text key={value} x={PAD_LEFT - 4} y={yAt(value) + 3} textAnchor="end" fontSize={8} fill="currentColor">
                {value}
              </text>
            ))}
          </g>

          {/* 等级折算线：虚线 + 弱化色，表示它是换算出来的参考线 */}
          <polyline
            points={toPath(cefrSeries)}
            fill="none"
            className="text-muted-foreground"
            stroke="currentColor"
            strokeWidth={1.4}
            strokeDasharray="4 3"
            strokeLinejoin="round"
          />

          {/* 综合得分线：主色实线，是主读的那条 */}
          {scoreSeries.length >= 2 && (
            <polyline
              points={toPath(scoreSeries)}
              fill="none"
              className="text-primary"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* 数据点。点少的时候画出来，只有 2 个点时也能看出"这是两个点连成的一条线" */}
          {scoreSeries.map((point) => (
            <circle
              key={`s-${point.x}-${point.score}`}
              cx={point.x}
              cy={point.y}
              r={2.4}
              className="text-primary"
              fill="currentColor"
            />
          ))}
          {cefrSeries.map((point) => (
            <circle
              key={`c-${point.x}-${point.cefr}`}
              cx={point.x}
              cy={point.y}
              r={2}
              className="text-muted-foreground"
              fill="currentColor"
            />
          ))}

          {/* 最新点：大一点 + 文字标注，用户一眼能看到"现在在哪" */}
          <circle cx={markerX} cy={markerY} r={3.6} className="text-primary" fill="currentColor" />
          <text
            x={markerX}
            y={markerY - 7}
            textAnchor="end"
            fontSize={9}
            className="text-foreground"
            fill="currentColor"
          >
            {markerText}
          </text>

          {/* 横轴只标首尾两个日期：点多的时候全标会糊成一片 */}
          <g className="text-muted-foreground">
            <text x={PAD_LEFT} y={HEIGHT - 8} textAnchor="start" fontSize={8} fill="currentColor">
              {formatShortDate(first.createdAt)}
            </text>
            <text x={WIDTH - PAD_RIGHT} y={HEIGHT - 8} textAnchor="end" fontSize={8} fill="currentColor">
              {formatShortDate(last.createdAt)}
            </text>
          </g>
        </svg>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-0.5 w-4 rounded-full bg-primary" />
            综合得分（0–100）
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-0.5 w-4 rounded-full bg-muted-foreground" />
            CEFR 档位折算
          </span>
          {scoreSeries.length < points.length && <span>（部分历史记录没有得分，未画入实线）</span>}
        </div>
      </CardBody>
    </Card>
  );
}
